/* `/orgs/members` and `/orgs/invites` — who is in the org, and the writes that
   change it. Wire: backend/modules/orgs/README.md.

   In `shared/api/` because two features write it: first-run setup sends invites,
   the Settings page manages members (rule book §1 rule 5).

   Paths are bare; `client.baseURL` carries `/api/v1`. Every response goes
   through `unwrap`. */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { client } from "./client";
import { unwrap, type Envelope } from "./envelope";

import type { MemberRow, PersonRef, RoleId } from "@/shared/types";

export interface Invite {
  user_id: string;
  email: string;
  role: RoleId;
  status: string;
  invite_expires_at: string;
}

export const memberKeys = {
  all: ["members"] as const,
};

/* ------------------------------ endpoints -------------------------------- */

export async function listMembers(): Promise<MemberRow[]> {
  return unwrap(await client.get<Envelope<MemberRow[]>>("/orgs/members"));
}

/** One address per call — the endpoint takes one, and a caller needs to say
    which address failed rather than which batch did. */
export async function inviteMember(email: string, role: "admin" | "member" = "member"): Promise<Invite> {
  return unwrap(await client.post<Envelope<Invite>>("/orgs/invites", { email, role }));
}

export async function revokeInvite(userId: string): Promise<{ user_id: string; status: string }> {
  return unwrap(await client.delete<Envelope<{ user_id: string; status: string }>>(`/orgs/invites/${userId}`));
}

export async function setMemberRole(userId: string, role: RoleId): Promise<{ user_id: string; role: RoleId }> {
  return unwrap(await client.patch<Envelope<{ user_id: string; role: RoleId }>>(`/orgs/members/${userId}`, { role }));
}

export async function removeMember(userId: string): Promise<{ user_id: string; status: string }> {
  return unwrap(await client.delete<Envelope<{ user_id: string; status: string }>>(`/orgs/members/${userId}`));
}

/* -------------------------------- hooks ---------------------------------- */

export function useMembers() {
  return useQuery({ queryKey: memberKeys.all, queryFn: listMembers });
}

/* Every write re-reads the list whatever the answer: a refusal (last owner,
   your own role) leaves the screen holding a change the server turned down. */
function useMembersMutation<V, T>(mutationFn: (vars: V) => Promise<T>) {
  const qc = useQueryClient();
  return useMutation({ mutationFn, onSettled: () => qc.invalidateQueries({ queryKey: memberKeys.all }) });
}

export const useInviteMember = () =>
  useMembersMutation((v: { email: string; role: "admin" | "member" }) => inviteMember(v.email, v.role));
export const useRevokeInvite = () => useMembersMutation(revokeInvite);
export const useSetMemberRole = () => useMembersMutation((v: { userId: string; role: RoleId }) => setMemberRole(v.userId, v.role));
export const useRemoveMember = () => useMembersMutation(removeMember);

/* ------------------------------- display --------------------------------- */

/** First and last name when the member has set them, else the address. */
export const memberName = (m: MemberRow): string =>
  [m.first_name, m.last_name].filter(Boolean).join(" ") || m.email;

/** A person the wire names (an agent's owner, who asked for a skill): their name, else the address. */
export const personName = (p: PersonRef): string => p.name || p.email;
